import * as THREE from 'three';
import { useRef, useCallback, useMemo } from 'react';
import { scaleSequential } from 'd3-scale';
import { interpolateTurbo } from 'd3-scale-chromatic';
import { Text } from '@react-three/drei';
import { getMetricValue } from './RTFOMapWithWellbore';
import { convertMetricValue, getMetricParamName } from './RTFOMetricsPanel';
import { coordinatesToCartesian3DPosition, intersectionLineCurve } from './RTFOWellbore3D';
import { stripMonitorUWI } from "./rtfoConfig";
import uuid from 'react-uuid';
import { setCurrentStageName } from '../../reducers/rtfoSlice';
import { useDispatch } from 'react-redux';

function convertToScaleWithAngle(value, scale, angle) {
  angle = THREE.MathUtils.degToRad(angle);
  const valueX = value * Math.cos(angle);
  const valueZ = value * Math.sin(angle);
  const convertedValueX = valueX / scale.getComponent(0);
  const convertedValueZ = valueZ / scale.getComponent(2);
  const finalValue = Math.hypot(convertedValueX, convertedValueZ);
  return finalValue;
};

export default function RTFOWellbore3DMesh({
  axisScale,
  points,
  stages,
  color,
  name,
  widthMultiplier,
  heightMultiplier,
  currentRTFO,
  metric,
  setMetric,
  stressShadow,
  metricMinMax,
  isMonitorWell,
  stageMonitorEvents,
  monitorWellsPoints,
  isFDI,
  allInterferenceLineCoordinates,
  hideFracGeometry,
  isMetricsMap,
  dailyStages,
  onlyHorizontalFase,
  curveSurvey,
  verticalScaleMultiplier,
  gridOffset,
  size,
}) {

  const ref = useRef();

  const dispatch = useDispatch();

  const curve = useMemo(() => {
    if (onlyHorizontalFase === true && curveSurvey !== undefined) {
      let hPoints = points?.filter((point) => {
        if (point?.y < gridOffset?.y + size / 2) {
          return true;
        }
        return false;
      });

      return new THREE.CatmullRomCurve3(hPoints, false);
    }

    return new THREE.CatmullRomCurve3(points, false);
  }, [curveSurvey, gridOffset?.y, onlyHorizontalFase, points, size]);

  const monitorWellsCurves = useMemo(() => {
    if (monitorWellsPoints !== undefined && monitorWellsPoints !== null) {
      return monitorWellsPoints.map((value) => {
        return new THREE.CatmullRomCurve3(value, false);
      });
    }
    else {
      return null;
    };
  }, [monitorWellsPoints]);

  const settings = currentRTFO?.settings;
  const units = settings?.units || 'metric';

  const Stages = useCallback(() => {
    if (Array.isArray(stages) && curve !== undefined) {
      const colorScale = scaleSequential(interpolateTurbo).domain(metricMinMax);
      return stages.map((value, index) => {
        const Lines = [];
        const stageName = `${currentRTFO?.name}_${name?.charAt(0)?.toUpperCase()?.concat(name?.substring(1,))}_${index + 1}`;

        const h = ((settings?.fracHeight || 60) / axisScale.getComponent(1));
        const midVector = new THREE.Vector3().addVectors(value[0], value[value.length - 1]).divideScalar(2);
        let w = (
          (currentRTFO?.stageData?.[stageName]?.fracModel?.halfLength * 2)
          ||
          (currentRTFO?.stageData?.[stageName]?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight || 60)
          ) || 600);

        const stageRotation = (Math.PI / 2 + THREE.MathUtils.degToRad(settings?.fracAngle || 45));

        w = convertToScaleWithAngle(w, axisScale, settings.fracAngle || 45) * widthMultiplier;

        const paramValue = +convertMetricValue(getMetricParamName(metric), getMetricValue(stageName, metric, stressShadow, currentRTFO?.stageData, currentRTFO?.settings, currentRTFO?.perfSchedule, widthMultiplier, currentRTFO?.stages, units, currentRTFO?.monitorEvents, []), units);

        //not change
        let length = 0;

        if (value.length === 1 && stages.length > 1) {
          length = 0.1;
        } else {
          length = new THREE.Vector2(
            value[0].getComponent(0),
            value[0].getComponent(2)
          ).distanceTo(
            new THREE.Vector2(
              value[value.length - 1].getComponent(0),
              value[value.length - 1].getComponent(2)
            )
          );
        };

        //Fix scaling in rotateY
        length *= Math.sin((currentRTFO?.settings?.fracAngle || 45) * Math.PI / 180);

        const geometry = !hideFracGeometry ? new THREE.BoxGeometry(w, h, length) : new THREE.BoxGeometry(0.3, 0.3, 0.3);
        const material = new THREE.MeshStandardMaterial({
          color:
            isMetricsMap && !dailyStages?.includes(stageName) ? 'grey' :
              isFDI ? (currentRTFO?.currentStageName === stageName ? 'tomato' : stageMonitorEvents?.find(e => e.stageName === stageName) ? 'blue' : 'lightgrey') :
                currentRTFO?.stageData?.[stageName] ? colorScale(paramValue) : 'lightgrey',
          transparent: true,
          opacity: currentRTFO?.stageData?.[stageName] ? 0.7 : 0.5
        });

        const mesh = new THREE.Mesh(geometry, material);
        //Euler and quartenion
        mesh.rotateY(stageRotation);
        mesh.position.set(...midVector.toArray());

        const tangent = curve.getTangent(1).normalize();
        const stageLabelAngle = Math.atan2(tangent.x, tangent.z);

        function handleStageClick(event) {
          event.stopPropagation();
          console.log(`Well ${name}, Stage ${index + 1}, colorRange ${paramValue}`);
          if (currentRTFO?.stages?.includes(stageName)) {
            dispatch(setCurrentStageName(stageName));
          };
        };

        try {
          if (isFDI === true && Object?.values(currentRTFO?.monitorSurveys)?.length > 0) {
            const monitorWellsNames = Object.keys(currentRTFO?.monitorSurveys);
            for (const stageEvent of stageMonitorEvents) {
              if (stageEvent.stageName === stageName) {
                // const wellMonitorIndex = monitorWellsNames.findIndex(e => e === stageEvent?.monitorUWI)
                const wellMonitorIndex = monitorWellsNames.findIndex(
                  uwi => currentRTFO?.client === 'marathon'
                    ?
                    stageEvent?.monitorUWI
                    :
                    stripMonitorUWI(uwi) === stripMonitorUWI(stageEvent?.monitorUWI));

                if (wellMonitorIndex === -1) continue;

                let intersection = intersectionLineCurve(
                  midVector,
                  -stageRotation,
                  monitorWellsCurves?.[wellMonitorIndex],
                  currentRTFO?.monitorSurveys?.[monitorWellsNames?.[wellMonitorIndex]]?.surveyData
                );

                if (intersection === null) {
                  intersection = intersectionLineCurve(
                    midVector,
                    -stageRotation - Math.PI,
                    monitorWellsCurves?.[wellMonitorIndex],
                    currentRTFO?.monitorSurveys?.[monitorWellsNames?.[wellMonitorIndex]]?.surveyData
                  );
                }

                if (
                  intersection === null &&
                  allInterferenceLineCoordinates !== undefined
                ) {
                  const intersectionCoord = allInterferenceLineCoordinates?.[stageEvent?.monitorUWI]?.[stageEvent?.stageName]?.[1];
                  const firstWell = Object.values(currentRTFO?.surveys?.wells)?.[0];
                  intersection = coordinatesToCartesian3DPosition(
                    [firstWell?.surfaceLon, firstWell?.surfaceLat],
                    [intersectionCoord?.[1], intersectionCoord?.[0]],
                    axisScale
                  );
                  const tempCurve = monitorWellsCurves?.[wellMonitorIndex];
                  intersection?.setY(tempCurve?.points?.[tempCurve?.points?.length - 1]?.y);
                }

                if (intersection !== undefined && intersection !== null) {
                  const arrowHelper = new THREE.ArrowHelper(
                    new THREE.Vector3().subVectors(intersection, midVector).normalize(),
                    midVector,
                    midVector.distanceTo(intersection) - 0.6,
                    0xff6347,
                    0.5,
                    0.5
                  )

                  function handleIntersectionClick(event) {
                    event.stopPropagation();
                    console.log("Stage Event: ", stageEvent);
                    console.log("Stage Monitor Well: ", monitorWellsNames?.[wellMonitorIndex]);
                  }

                  Lines.push(
                    <group>
                      <primitive
                        object={arrowHelper}
                        key={uuid()}
                        name="ArrowHelper"
                      />
                      <mesh
                        position={intersection?.toArray()}
                        onClick={handleIntersectionClick}
                      >
                        <sphereGeometry
                          args={[0.3, 32, 32]}
                        />
                        <meshStandardMaterial color={"tomato"} />
                      </mesh>
                    </group>
                  );
                };
              };
            };

          };
        }
        catch (err) {
          console.log(err);
        }

        return (
          <group
            key={name + index}
            name="wellStageGroup"
          >
            {Lines.length > 0 ? Lines : null}
            <Text
              position={midVector.clone().add(new THREE.Vector3(0, !hideFracGeometry ? (h / 1.5) + 2 : 1, 0))}
              rotation={[0, stageLabelAngle, 0]}
              color="white"
              anchorX="center"
              anchorY="middle"
              outlineBlur="15%"
            >
              {index + 1}
            </Text>
            <primitive
              object={mesh}
              ref={ref}
              key={name + index}
              onClick={handleStageClick}
              name="wellStage"
            />
          </group>
        );
      });
    };

  }, [stages, curve, metricMinMax, currentRTFO?.name, currentRTFO?.stageData, currentRTFO?.settings, currentRTFO?.perfSchedule, currentRTFO?.stages, currentRTFO?.monitorEvents, currentRTFO?.currentStageName, currentRTFO?.monitorSurveys, currentRTFO?.client, currentRTFO?.surveys?.wells, name, settings?.fracHeight, settings.fracAngle, axisScale, widthMultiplier, metric, stressShadow, units, hideFracGeometry, isMetricsMap, dailyStages, isFDI, stageMonitorEvents, dispatch, monitorWellsCurves, allInterferenceLineCoordinates]);

  function Mesh() {
    if (points !== null && curve !== undefined) {
      const geometry = new THREE.TubeGeometry(curve, points.length * 10, 0.1, 50, false);
      const material = new THREE.MeshStandardMaterial({
        color
      });
      const mesh = new THREE.Mesh(geometry, material);

      const lastPoint = curve.getPoint(1);
      const tangent = curve.getTangent(1).normalize();
      const distance = 2;
      const offsetPoint = new THREE.Vector3()
        .addVectors(lastPoint, tangent.multiplyScalar(distance))
        .add(new THREE.Vector3(0, (((settings.fracHeight || 45) / axisScale.getComponent(1)) / 1.5) + 3, 0));
      const angle = Math.atan2(tangent.x, tangent.z);

      return (
        <group>
          <primitive object={mesh} ref={ref} />
          <Stages />
          <Text
            color="white"
            outlineBlur="15%"
            anchorX="center"
            anchorY="middle"
            position={offsetPoint}
            scale={[1, 1, 1]}
            rotation={[0, angle, 0]}
          >
            {isMonitorWell ? (currentRTFO?.monitorAlias?.[name] || name) : (name.charAt(0).toUpperCase() + name.slice(1))}
          </Text>
        </group>
      );
    } else return null;
  };

  if (
    !axisScale ||
    axisScale === undefined ||
    !points ||
    points === undefined ||
    (!stages && !isMonitorWell) ||
    (stages === undefined && !isMonitorWell) ||
    !color ||
    color === undefined ||
    !name ||
    name === undefined
  ) {
    return null;
  };

  return <Mesh />;
};

// function calculateStageLength(currentRTFO, stageName) {
//   try {
//     const wellName = stageName?.split('_')?.[1]?.toLowerCase();
//     const stageNumber = String(Number(String(stageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));

//     // determine stage length
//     const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
//     const clusterNames = stageClusters && Object.keys(stageClusters);
//     const numClusters = clusterNames?.length || 0;

//     if (!clusterNames || !numClusters > 0) return 0;

//     const lastStageNumber = Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0];

//     // Calculate stage length
//     let stageLength = 0;

//     if (stageNumber === '1') { // for first stage
//       stageLength = stageClusters?.['1']?.shotDepth - currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.['2']?.clusters?.['1']?.shotDepth;
//     } else if (stageNumber === lastStageNumber) { // for last stage
//       const nextToLastStageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(stageNumber) - 1]?.clusters;
//       stageLength = nextToLastStageClusters?.[Object.keys(nextToLastStageClusters)?.slice(-1)?.[0]]?.shotDepth - stageClusters?.[clusterNames?.slice(-1)?.[0]]?.shotDepth;
//     } else { // for all other stages
//       const previousStageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(stageNumber) - 1]?.clusters;
//       const nextStageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(stageNumber) + 1]?.clusters;
//       const topMidPoint = (previousStageClusters?.[Object.keys(previousStageClusters)?.slice(-1)?.[0]]?.shotDepth + stageClusters?.['1']?.shotDepth) / 2;
//       const bottomMidPoint = (stageClusters?.[clusterNames?.slice(-1)?.[0]]?.shotDepth + nextStageClusters?.['1']?.shotDepth) / 2;
//       stageLength = topMidPoint - bottomMidPoint;
//     };

//     // Calculate cluster spacing
//     let clusterSpacing = 0;

//     if (numClusters === 1) { // for single-cluster stage
//       clusterSpacing = stageLength;
//     } else { // for all other stages
//       clusterSpacing = (stageClusters?.['1']?.shotDepth - stageClusters?.[clusterNames?.slice(-1)?.[0]]?.shotDepth) / (numClusters - 1);
//     };

//     if (!stageLength > 0) {
//       stageLength = clusterSpacing * numClusters;
//     };

//     return stageLength;
//   } catch (error) {
//     console.log(error);
//     return 0;
//   };
// };
